// ExpandableSearch.jsx
import React, { useRef, useState, useEffect } from "react";
import {
  IconButton,
  InputBase,
  Paper,
  ClickAwayListener,
  Box, InputAdornment, Typography
} from "@mui/material";
import { Search, Close } from "@mui/icons-material";

/**
 * ExpandableSearch
 *
 * Props:
 * - onSearch(value)        => called when user presses Enter
 * - placeholder (string)
 * - initialOpen (boolean)
 * - size ("small"|"medium") - controls InputBase size visuals
 */
export default function ExpandableSearch({
  onSearch,
  placeholder = "Search...",
  initialOpen = false,
  size = "small",
  search = ""
}) {
  const [open, setOpen] = useState(initialOpen);
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  // auto focus when opening
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleToggle = () => {
    if(open && search != value)
        onSearch(value);
    setOpen(!open);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      if(search != value)
        onSearch(value);
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      onSearch?.(value);
    }
  };

  const handleClickAway = () => {
    if (open) setOpen(false);
    if(search != value)
        onSearch(value);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1, verticalAlign: 'middle'
          // ensure it fits well in AppBar or toolbar
        }}
      >
        {/* Animated container */}
        <Paper
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            onSearch?.(value);
          }}
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            bgcolor: "transparent",
            borderRadius: 2,
            transition: "width 220ms ease, background-color 220ms ease",
            width: open ? { xs: "calc(100vw - 23px)", md: "260px" } : "30px",
            height: '30px',
            boxShadow: open ? 3 : "none",
            border: (theme) =>
              open ? `1px solid ${theme.palette.divider}` : "none",
            px: open ? 0.2 : 0.25,
            overflow: "hidden",
            // mr: 0.5,
          }}
        >
          <IconButton
            size={size}
            onClick={handleToggle}
            aria-label={open ? "Close search" : "Open search"}
            sx={{
              p: open ? 0.5 : 0.5,
              color: "inherit", height: '30px'
            }}
          >
            {open ? <Close fontSize="small" /> : <Search fontSize="small" sx={{color: (value?"orange":"")}} />}
          </IconButton>

          {/* show input only when open (keeps DOM small) */}
          {open && (
            <InputBase
              inputRef={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              sx={{
                ml: 0.2, mr: 0.5,
                flex: 1,
                fontSize: {xs: 16, sm: 14},
              }}
              inputProps={{
                "aria-label": "search input",
              }}
              endAdornment={
                value?
                <InputAdornment position="end">
                  <Typography sx={{color: '#ff9b9b', fontStyle: 'italic', cursor: 'pointer'}} 
                    onClick={()=>{setValue(''); inputRef.current.focus();}}>xóa</Typography>
                </InputAdornment>
                :''
              }
            />
          )}
        </Paper>
      </Box>
    </ClickAwayListener>
  );
}
